import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const PasswordlessAuthContext = createContext();

export const usePasswordlessAuth = () => {
  const context = useContext(PasswordlessAuthContext);
  if (!context) {
    throw new Error('usePasswordlessAuth must be used within a PasswordlessAuthProvider');
  }
  return context;
};

export const PasswordlessAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is authenticated on app load
  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/auth/me');
      setUser(response.data.user);
      setError(null);
    } catch (err) {
      // If auth check fails, try to get token from localStorage as fallback
      const token = localStorage.getItem('passwordless_token');
      if (token) {
        try {
          const response = await axios.get('/api/auth/me', {
            headers: {
              Authorization: `Bearer ${token}`
            }
          });
          setUser(response.data.user);
          setError(null);
        } catch (fallbackErr) {
          // Token is invalid, remove it
          localStorage.removeItem('passwordless_token');
          setUser(null);
          setError(null);
        }
      } else {
        setUser(null);
        setError(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      // Clear localStorage tokens
      localStorage.removeItem('passwordless_token');
      localStorage.removeItem('token');
      
      // Clear cookies (this will be done by the browser when we redirect)
      document.cookie = 'token=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
      
      setUser(null);
      setError(null);
      
      // Redirect to login
      window.location.href = '/passwordless-login';
    } catch (err) {
      console.error('Logout error:', err);
    }
  };

  const requestMagicLink = async (email, firstName = '', lastName = '') => {
    try {
      setError(null);
      const response = await axios.post('/api/auth/request-magic-link', {
        email,
        firstName,
        lastName
      });
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to send magic link';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  const verifyMagicLink = async (token, remember = false) => {
    try {
      setError(null);
      const response = await axios.post('/api/auth/verify-magic-link', {
        token,
        remember
      });
      
      setUser(response.data.user);
      
      // Store token in localStorage as fallback
      if (response.data.token) {
        // Store in both new and legacy keys so old pages work without changes
        localStorage.setItem('passwordless_token', response.data.token);
        localStorage.setItem('token', response.data.token);
      }
      
      return response.data;
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to verify magic link';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  };

  // Helper function to get auth headers for API calls
  const getAuthHeaders = () => {
    const token = localStorage.getItem('passwordless_token') || localStorage.getItem('token');
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  // Helper function to make authenticated API calls
  const authenticatedRequest = async (config) => {
    const headers = {
      ...config.headers,
      ...getAuthHeaders()
    };

    return axios({
      ...config,
      headers
    });
  };

  const value = {
    user,
    loading,
    error,
    isAuthenticated: !!user,
    checkAuth,
    logout,
    requestMagicLink,
    verifyMagicLink,
    getAuthHeaders,
    authenticatedRequest,
    setError
  };

  return (
    <PasswordlessAuthContext.Provider value={value}>
      {children}
    </PasswordlessAuthContext.Provider>
  );
}; 